import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import {
  ArrowDownRight,
  ArrowUpRight,
  Award,
  Check,
  GraduationCap,
  Sparkles,
} from 'lucide-react';

import { Counter } from '@/components/initiative/Counter';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { CoachApplicationForm } from '@/components/initiative/CoachApplicationForm';
import { scholarshipSources, coachFaq } from '@/data/mock/initiative';
import { cn } from '@/lib/utils';

const ELIGIBILITY_KEYS: Array<{ ar: string; en: string }> = [
  {
    ar: 'مدرب كرة قدم مقيم داخل قطاع غزة حالياً',
    en: 'Football coach currently residing inside Gaza',
  },
  {
    ar: 'تحمل ترخيصاً ساري المفعول من الفيفا أو الاتحاد الفلسطيني',
    en: 'Hold a valid license from FIFA or the Palestinian Football Federation',
  },
  {
    ar: 'مستوى الترخيص بين PRO و D',
    en: 'License level is between PRO and D',
  },
  {
    ar: 'قادر على إكمال الكورسات أونلاين خلال 70 يوماً',
    en: 'Able to complete the courses online within 70 days',
  },
];

export default function InitiativeCoaches() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const lang = isRTL ? 'ar' : 'en';

  const nextSteps = t('initiative.coaches.nextSteps', { returnObjects: true }) as Array<{
    title: string;
    body: string;
  }>;

  const ArrowIcon = isRTL ? ArrowDownRight : ArrowUpRight;
  const heroRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start'],
  });
  const backdropScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const backdropY = useTransform(scrollYProgress, [0, 1], ['0%', '12%']);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [1, 1.4]);
  const contentY = useTransform(scrollYProgress, [0, 1], ['0%', '-8%']);
  const scrollProgress = useTransform(scrollYProgress, [0, 1], ['0%', '100%']);

  return (
    <div className="initiative-theme font-body-init overflow-x-hidden bg-background">
      {/* ───────── HERO (magazine-cover, compact) ───────── */}
      <section
        ref={heroRef}
        className="relative isolate pt-8 pb-16 lg:pb-20 overflow-hidden"
      >
        <motion.img
          aria-hidden
          src="/img/initiative/hero-coaches.webp"
          alt=""
          style={{ scale: backdropScale, y: backdropY }}
          className="absolute inset-0 h-full w-full object-cover opacity-50 pointer-events-none"
          loading="eager"
        />
        <motion.div
          aria-hidden
          style={{ opacity: overlayOpacity }}
          className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/30 to-background/90 pointer-events-none"
        />
        <div aria-hidden className="absolute inset-0 bg-line-grid opacity-20 pointer-events-none" />

        <motion.div
          aria-hidden
          animate={{ scale: [1, 1.18, 1], opacity: [0.2, 0.45, 0.2], x: [0, 30, 0] }}
          transition={{ duration: 16, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute -top-32 -right-32 w-[44rem] h-[44rem] bg-accent/12 rounded-full blur-3xl pointer-events-none"
        />
        <motion.div
          aria-hidden
          animate={{ scale: [1.1, 1, 1.1], opacity: [0.12, 0.3, 0.12], x: [0, -40, 0] }}
          transition={{ duration: 22, repeat: Infinity, ease: 'easeInOut', delay: 5 }}
          className="absolute -bottom-24 -left-32 w-[40rem] h-[40rem] bg-primary-foreground/10 rounded-full blur-3xl pointer-events-none"
        />

        {/* Publication ribbon */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative z-10"
        >
          <div className="container mx-auto px-4">
            <div className="flex flex-wrap items-center justify-between gap-3 py-3 border-y border-border/40 bg-background/30 backdrop-blur-sm rounded-sm">
              <span className="font-mono-mark text-[10px] uppercase tracking-[0.25em] text-foreground/85 font-bold">
                ★ {t('initiative.name')} — {t('initiative.slogan')}
              </span>
              <Link
                to="/initiative"
                className="font-mono-mark text-[10px] uppercase tracking-[0.3em] text-muted-foreground hover:text-accent transition-colors inline-flex items-center gap-1.5"
              >
                {isRTL ? 'العودة للمبادرة' : 'Back to initiative'}
                <ArrowIcon className="h-3 w-3" />
              </Link>
            </div>
          </div>
        </motion.div>

        <motion.div
          style={{ y: contentY }}
          className="container mx-auto px-4 relative z-10 pt-10 lg:pt-14"
        >
          {/* Launch badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.15, ease: [0.2, 0.8, 0.2, 1] }}
            className="flex justify-center mb-6"
          >
            <motion.div
              animate={{ y: [0, -2, 0] }}
              transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut' }}
              className="inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-full bg-accent/12 border border-accent/40 backdrop-blur-sm"
            >
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-accent opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-accent" />
              </span>
              <span className="font-mono-mark text-[10px] uppercase tracking-[0.25em] text-accent font-bold">
                № 03 · {isRTL ? 'تقدّم للمنحة' : 'Apply'}
              </span>
              <span className="hidden sm:inline-block h-2.5 w-px bg-accent/40" />
              <span className="hidden sm:inline font-editorial-italic text-[10px] text-accent/80">
                {t('initiative.launchLabel')} · {t('initiative.launchDateShort')}
              </span>
            </motion.div>
          </motion.div>

          {/* Headline — 2 lines */}
          <motion.h1
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.2 }}
            className={cn(
              'font-display font-black uppercase text-balance text-center max-w-5xl mx-auto',
              'text-4xl sm:text-5xl lg:text-6xl xl:text-7xl',
              isRTL
                ? 'font-display-ar normal-case leading-[1.4] py-1'
                : 'leading-[0.95]'
            )}
          >
            {(() => {
              const lines = isRTL
                ? [
                    { text: 'تقدّم لمنحة تدريب', accent: false },
                    { text: 'مجانية لمدربي غزة.', accent: true },
                  ]
                : [
                    { text: 'Apply for a', accent: false },
                    { text: 'free coaching scholarship.', accent: true },
                  ];
              return lines.map((line, i) => (
                <motion.span
                  key={i}
                  initial={{ opacity: 0, y: 30, filter: 'blur(8px)' }}
                  animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                  transition={{
                    duration: 0.7,
                    delay: 0.3 + i * 0.12,
                    ease: [0.2, 0.8, 0.2, 1],
                  }}
                  className={cn(
                    'block',
                    isRTL && 'pb-1',
                    line.accent &&
                      'bg-gradient-to-r from-accent via-accent to-accent/70 bg-clip-text text-transparent'
                  )}
                >
                  {line.text}
                </motion.span>
              ));
            })()}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="mt-5 font-editorial-italic text-base sm:text-lg text-foreground/75 leading-relaxed text-center max-w-2xl mx-auto"
          >
            "{t('initiative.coaches.heroSubtitle')}"
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="mt-7 flex flex-wrap items-center justify-center gap-3"
          >
            <a
              href="#application-form"
              className="group inline-flex items-center gap-2 h-11 px-6 rounded-md bg-accent text-accent-foreground font-bold uppercase tracking-[0.18em] text-xs btn-glow transition-transform hover:-translate-y-0.5"
            >
              <GraduationCap className="h-4 w-4" />
              {isRTL ? 'إلى نموذج الطلب' : 'Go to application'}
              <ArrowIcon className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
            </a>
            <Link
              to="/initiative/sponsor"
              className="group inline-flex items-center gap-2 h-11 px-5 rounded-md border-2 border-foreground/20 hover:border-accent text-xs font-bold uppercase tracking-[0.18em] text-foreground/85 hover:text-accent transition-colors"
            >
              <Sparkles className="h-4 w-4 text-accent" />
              {isRTL ? 'لست مدرّباً؟ ارعَ' : 'Not a coach? Sponsor'}
            </Link>
          </motion.div>
        </motion.div>

        {/* Bottom stats data strip */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.95 }}
          className="relative z-10 mt-10 lg:mt-14"
        >
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 border-y border-border/40 bg-background/30 backdrop-blur-sm rounded-sm divide-x divide-border/40 rtl:divide-x-reverse">
              {[
                { num: 150, label: isRTL ? 'منحة مجانية' : 'Free scholarships', highlight: true },
                { code: 'PRO–D', label: isRTL ? 'مستويات الترخيص' : 'License levels' },
                { num: 25, label: isRTL ? 'كورس مختار' : 'Curated courses' },
                { date: '6·6', label: isRTL ? 'يوم الانطلاق' : 'Launch day' },
              ].map((s, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.45, delay: 1.05 + i * 0.06 }}
                  whileHover={{ y: -2 }}
                  className="group relative px-4 py-4 text-center"
                >
                  <div
                    className={cn(
                      'font-display font-black text-3xl lg:text-4xl leading-none num-tabular transition-colors',
                      s.highlight
                        ? 'bg-gradient-to-b from-accent via-accent to-accent/70 bg-clip-text text-transparent'
                        : 'text-foreground group-hover:text-accent'
                    )}
                  >
                    {s.code ? s.code : s.date ? s.date : <Counter value={s.num as number} />}
                  </div>
                  <div className="mt-2 font-mono-mark text-[9px] uppercase tracking-[0.22em] text-muted-foreground">
                    {s.label}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        <motion.div
          aria-hidden
          style={{ width: scrollProgress }}
          className="absolute bottom-0 left-0 h-px bg-gradient-to-r from-accent via-accent/80 to-transparent z-20"
        />
      </section>

      {/* ELIGIBILITY + WHAT'S INCLUDED */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto items-stretch">
            {/* ELIGIBILITY */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="p-7 rounded-2xl bg-gradient-to-br from-primary/10 to-primary/5 border-2 border-primary/30 backdrop-blur-xl h-full"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="h-11 w-11 rounded-xl bg-card/90 flex items-center justify-center">
                  <GraduationCap className="h-5 w-5 text-primary" />
                </div>
                <h2 className="text-2xl font-bold text-foreground">
                  {t('initiative.coaches.eligibilityTitle')}
                </h2>
              </div>
              <p className="text-sm text-foreground/75 mb-5">
                {t('initiative.coaches.eligibilitySubtitle')}
              </p>
              <ul className="space-y-3">
                {ELIGIBILITY_KEYS.map((item, i) => (
                  <li key={i} className="flex gap-3 text-sm text-foreground/85">
                    <div className="mt-0.5 h-5 w-5 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Check className="h-3 w-3 text-accent" />
                    </div>
                    <span className="leading-relaxed">{item[lang]}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* WHAT'S INCLUDED */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="p-7 rounded-2xl bg-gradient-to-br from-accent/10 to-accent/5 border-2 border-accent/30 backdrop-blur-xl h-full"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="h-11 w-11 rounded-xl bg-card/90 flex items-center justify-center">
                  <Award className="h-5 w-5 text-accent" />
                </div>
                <h2 className="text-2xl font-bold text-foreground">
                  {t('initiative.coaches.includedTitle')}
                </h2>
              </div>
              <p className="text-sm text-foreground/75 mb-5">
                {t('initiative.coaches.includedSubtitle')}
              </p>
              <ul className="space-y-3">
                {scholarshipSources.map((src) => (
                  <li
                    key={src.id}
                    className={cn(
                      'flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg',
                      src.highlight ? 'bg-accent/15 border border-accent/40' : 'bg-card/60'
                    )}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="h-9 w-12 flex-shrink-0 flex items-center justify-center">
                        {src.logo ? (
                          <img
                            src={src.logo}
                            alt={src.name.en}
                            className="max-h-8 max-w-full w-auto object-contain"
                          />
                        ) : (
                          <span
                            className={cn(
                              'inline-flex items-center px-1.5 py-0.5 rounded font-display font-black tracking-tight text-[10px] border',
                              src.highlight
                                ? 'text-accent border-accent/40 bg-accent/10'
                                : 'text-foreground/80 border-border/60 bg-surface-2/50'
                            )}
                          >
                            BiHUB
                          </span>
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-bold text-foreground truncate">
                          {src.name[lang]}
                        </div>
                        <div className="text-[11px] text-muted-foreground truncate">
                          {src.platform[lang]}
                        </div>
                      </div>
                    </div>
                    <div className="text-base font-bold text-accent flex-shrink-0">
                      ×{src.count}
                    </div>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* APPLICATION FORM */}
      <section className="py-20 bg-surface-1/30">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-center mb-10"
            >
              <div className="inline-flex h-14 w-14 rounded-2xl bg-accent items-center justify-center mb-4 shadow-lg shadow-accent/30">
                <Sparkles className="h-7 w-7 text-primary" />
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold gradient-text">
                {t('initiative.coaches.formTitle')}
              </h2>
              <p className="mt-3 text-foreground/75">
                {t('initiative.coaches.formSubtitle')}
              </p>
            </motion.div>

            <div className="p-6 sm:p-8 rounded-2xl bg-card/80 border border-border/60 backdrop-blur-xl">
              <CoachApplicationForm />
            </div>
          </div>
        </div>
      </section>

      {/* NEXT STEPS */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12 max-w-2xl mx-auto"
          >
            <h2 className="text-3xl sm:text-4xl font-bold gradient-text">
              {t('initiative.coaches.nextStepsTitle')}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
            {nextSteps.map((step, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.08 }}
                className="relative p-5 rounded-2xl bg-card/80 border border-border/60 backdrop-blur-xl"
              >
                <div className="text-2xl font-bold gradient-text">
                  {step.title}
                </div>
                <p className="mt-2 text-sm text-foreground/80 leading-relaxed">
                  {step.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-surface-1/30">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-10 max-w-2xl mx-auto"
          >
            <h2 className="text-3xl sm:text-4xl font-bold gradient-text">
              {t('initiative.coaches.faqTitle')}
            </h2>
          </motion.div>

          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-3">
              {coachFaq.map((item, idx) => (
                <AccordionItem
                  key={idx}
                  value={`item-${idx}`}
                  className="rounded-xl border border-border/60 bg-card/80 backdrop-blur-xl px-5"
                >
                  <AccordionTrigger className="text-base font-semibold text-foreground hover:no-underline">
                    {item.question[lang]}
                  </AccordionTrigger>
                  <AccordionContent className="text-foreground/80 leading-relaxed">
                    {item.answer[lang]}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>
    </div>
  );
}
